import React, {
  useRef, useCallback, useState, useEffect, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import {
  Drawer,
  Form,
  Col,
  Row,
  Select,
  DatePicker,
  Button,
  message,
} from 'antd';
import {
  FormOutlined, InfoCircleTwoTone, SettingOutlined, SaveOutlined,
} from '@ant-design/icons';
import { DateTime } from 'luxon';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

import { Common, Schedule } from 'ontraccr-common';
import OnTraccrTextInput from '../common/inputs/OnTraccrTextInput';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import DisplayText from '../common/text/DisplayText';
import DivisionSelector from '../common/inputs/DivisionSelector';
import FileReelWithUpload from '../common/files/FileReelWithUpload';
import BorderlessButton from '../common/buttons/BorderlessButton';
import OnTraccrDatePicker from '../common/inputs/OnTraccrDatePicker';
import OntraccrTag from '../common/OntraccrTag';

import {
  reformatRemindersFromValues,
  timestampToPosition,
  dateRangeValidator,
  getDayKey,
  createShallowShift,
  addShallowShiftToShifts,
  getMaxStartFromRestriction,
  validateExpectedUsers,
  reformatExternalReminders,
  convertFromOneTimezoneToAnother,
} from './scheduleHelpers';
import ScheduleUserSelector from './ScheduleUserSelector';
import ScheduleEventForm from './ScheduleEventForm';

import {
  toggleShiftView, getShiftGroup, getSchedule, getGanttScheduleRow,
} from './state/schedule.actions';

import {
  filterSelectDropdown, getId, getIdMap, isNullOrUndefined,
} from '../helpers/helpers';
import { validateRepeatEndDate } from '../helpers/subtaskHelpers';

import config from '../config';
import ScheduleDateRepeatSelector from './ScheduleDateRepeatSelector';
import FormColorPicker from '../common/inputs/FormColorPicker';
import { REMINDER_OPTIONS } from './reminder/reminder.constants';
import ScheduleDatePicker from './ScheduleDatePicker';
import WeeklySchedule from './WeeklySchedule/WeeklySchedule';
import DailySchedule from './DailySchedule/DailySchedule';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import ScheduleTeamSelector from './ScheduleTeamSelector';
import { formatProjectLabelFromCompanySettings } from '../projects/projectHelpers';

import Permissions from '../auth/Permissions';
import ScheduleExpectedUsersInput from './ScheduleExpectedUsersInput';
import HoverHelp from '../common/HoverHelp';
import GanttScheduleAddDrawer from './GanttSchedule/GanttScheduleAddDrawer';
import ShiftUploadDrawer from './ShiftUpload/ShiftUploadDrawer';
import ScheduleReminderDrawer from './ScheduleReminderDrawer';
import { formSectionsPropType } from '../forms/forms.propTypes';
import ScheduleApplicantSelector from './ScheduleApplicantSelector';
import OnTraccrCheckbox from '../common/inputs/OnTraccrCheckbox';
import OnTraccrSwitch from '../common/inputs/OnTraccrSwitch';
import TimezoneSelector from '../common/inputs/TimezoneSelector';
import { getProjectPhases, UNPHASED, unphasedCode } from '../clock/ManualEntry/manualEntryHelpers';
import CostcodesTreeSelector from '../costcodes/CostcodesTreeSelector';
import MaterialTableAddDrawer from '../forms/FormBuilder/FormFields/MaterialTableAddDrawer';
import { formatEquipmentLabelFromCompanySettings } from '../equipment/equipment.helpers';
import {
  getFilteredEquipment,
  getFilteredMaterials,
} from './GanttSchedule/ganttScheduleHelpers';

import useProjectOrGlobalCostcodes from '../costcodes/useProjectOrGlobalCostcodes';

const formLabelStyle = {
  style: {
    paddingBottom: 5,
    marginTop: 5,
  },
};

const updateForm = ({ ref, data }) => {
  if (!ref) return;
  ref.setFieldsValue(data);
};

const basicOptions = (arr = [], extractor = (item) => item.name) => (
  arr.map((item) => (
    <Select.Option key={item.id} label={extractor(item)} value={item.id} name={item.name} number={item.number}>
      {extractor(item)}
    </Select.Option>
  ))
);

const getFormView = ({ isDisplay, value, input }) => {
  if (isDisplay) return <DisplayText title={value} />;
  return input;
};

const getFromMap = ({
  id,
  ids,
  idMap,
  formatter = (item) => item.name,
  defaultValue = null,
}) => {
  if (ids) {
    return ids.map((mapId) => (mapId in idMap ? formatter(idMap[mapId]) : defaultValue)).join(', ');
  }
  return id in idMap ? formatter(idMap[id]) : defaultValue;
};

/**
 * Removes undefined/null reminders
 * @param {array} reminders
 * @returns {array}
 */
const refineReminders = (newReminders = []) => newReminders.filter((reminder) => reminder);

/**
 * Removes external reminders with null/undefined value or no recipients, and formats values
 * @param {array} reminders
 * @returns {array}
 */
const refineExternalReminders = (newExternalReminders = []) => newExternalReminders
  .filter((reminder) => reminder?.value && reminder?.recipients?.length)
  .map(({ value, recipients }) => ({ value: value?.value, recipients }));

export default function ScheduleDrawer({
  isNew,
  visible,
  onClose,
  onSave,
  onDelete,
  shift,
  isDisplay,
  openFormDetail,
  shifts = {},
  shiftMap = {},
  lastDownload = [],
  setLastDownload,
  style = {},
  formStyle = {},
  hideCalendar = false,
  isGanttDispatch = false,
  isShiftTable = false,
  onApply,
  divisionId: propDivisionId,
  projectId: propProjectId,
  projectLocked,
  reminderConfig,
  sections = [],
}) {
  const {
    id,
    title,
    day,
    startTime,
    endTime,
    projectId,
    phaseId,
    costcodeId,
    description,
    divisionId,
    users = [],
    expectedUsers,
    files: shiftFiles,
    formId,
    isEvent: shiftIsEvent,
    formTemplateId,
    formTemplateName,
    formNumber,
    color,
    reminders = [],
    externalReminders = [],
    repeat,
    repeatEndDate,
    groupId,
    shouldLockClockIn = false,
    teams,
    ganttScheduleRowId,
    isDraft,
    appliedUsers: newAppliedUsers,
    shiftCategoryId: categoryId,
    timezone: shiftTimezone,
  } = shift || {};

  const timezone = shiftTimezone ?? DateTime.local().zoneName;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const divisions = useSelector((state) => state.settings.divisions);
  const { settings = {} } = useSelector((state) => state.settings.company);
  const stateTeams = useSelector((state) => state.teams.teams);
  const scheduleRestriction = useSelector((state) => state.settings.scheduleRestriction);
  const userToLabel = useSelector((state) => state.users.userToLabel);
  const { ganttScheduleRows = [] } = useSelector((state) => state.schedule);
  const categories = useSelector((state) => state.schedule.categories);
  const materials = useSelector((state) => state.materials.materials);
  const equipment = useSelector((state) => state.equipment.equipment);

  const {
    enableGlobalCCOnProject,
  } = settings;

  const positionMap = useSelector((state) => {
    const {
      settings: {
        positionNames = [],
      } = {},
    } = state;
    return getIdMap(positionNames);
  });

  const userLabelMap = useSelector((state) => {
    const { labels = [] } = state;
    const userLabels = labels.filter((label) => label.type === 'users');
    return getIdMap(userLabels);
  });

  const stateUsers = useSelector((state) => state.users.users);
  const stateProjects = useSelector((state) => state.projects.projects);

  const activeUsers = useMemo(() => (
    stateUsers.filter((user) => user.active)
  ), [stateUsers]);
  const userMap = useMemo(() => getIdMap(stateUsers), [stateUsers]);

  const activeProjects = useMemo(() => (
    stateProjects.filter((project) => project.active)
  ), [stateProjects]);
  const projectMap = useMemo(() => getIdMap(stateProjects), [stateProjects]);
  const categoryMap = useMemo(() => getIdMap(categories), [categories]);

  const {
    activePhases,
    costcodes: reduxCostcodes,
  } = useSelector((state) => {
    const {
      costcodes: {
        phases: statePhases = [],
        costcodeIdMap = {},
        costcodes: stateCostcodes = [],
      } = {},
    } = state;
    return {
      activePhases: statePhases,
      costcodes: stateCostcodes,
    };
  });

  const groupShifts = useSelector((state) => state.schedule.groupShifts);
  const originalShift = useMemo(() => {
    if (!groupId || !groupShifts) return false;
    const list = Object.values(groupShifts);
    const ourShifts = list.filter((s) => s.groupId === groupId);
    const orderedShifts = ourShifts.sort((a, b) => (a.startTime - b.startTime));
    return orderedShifts[0];
  }, [groupShifts, groupId]);

  const formRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [selectedProject, setSelectedProject] = useState(projectId || propProjectId);
  const [selectedPhase, setSelectedPhase] = useState(phaseId === null && costcodeId ? 'Unphased' : phaseId);
  const [selectedCC, setSelectedCC] = useState(costcodeId);
  const [dateRange, setDateRange] = useState();
  const [selectedDivision, setSelectedDivision] = useState(divisionId || propDivisionId);
  const [isEvent, setIsEvent] = useState(shiftIsEvent);
  const [formData, setFormData] = useState();
  const [saveIsLoading, setSaveIsLoading] = useState(false);
  const [draftIsLoading, setDraftIsLoading] = useState(false);
  const [repeatValue, setRepeatValue] = useState(repeat);
  const [showGanttDrawer, setShowGanttDrawer] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState(users);

  const [date, setDate] = useState(day || DateTime.local());
  const [viewType, setViewType] = useState('Week');
  const [localShifts, setLocalShifts] = useState(shifts ?? {});
  const [shiftShadow, setShiftShadow] = useState(shift ?? {});
  const [selectedTeams, setSelectedTeams] = useState(teams || []);
  const [showUploadDrawer, setShowUploadDrawer] = useState(false);
  const [showRemindersDrawer, setShowRemindersDrawer] = useState(false);
  const [shiftStatus, setShiftStatus] = useState();
  const [appliedUsers, setAppliedUsers] = useState(newAppliedUsers);
  const [selectedCategoryId, setSelectedCategoryId] = useState(categoryId);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [showMaterialDrawer, setShowMaterialDrawer] = useState(false);

  const { costcodes: activeCostcodes } = useProjectOrGlobalCostcodes({
    projectId: selectedProject,
    divisionId: selectedDivision,
    visible,
  });

  // HARBOUR-9379: only for tree select until HARBOUR-9255 is merged
  const activeCostcodesRedux = useMemo(() => (
    reduxCostcodes.filter((cc) => cc.active && cc.divisionId === selectedDivision)
  ), [reduxCostcodes, selectedDivision]);

  const costcodeMap = useMemo(() => getIdMap(activeCostcodesRedux), [activeCostcodesRedux]);

  useEffect(() => {
    const currentUserStatus = appliedUsers?.find((u) => u.userId === Permissions.id)?.status;
    setShiftStatus(currentUserStatus);
  }, [shift, appliedUsers]);

  useEffect(() => {
    if (newAppliedUsers) {
      setAppliedUsers(newAppliedUsers);
    }
  }, [newAppliedUsers, shift]);

  const weeksRestriction = useMemo(() => {
    const ourUser = activeUsers.find((u) => u.id === Permissions.id);
    return scheduleRestriction[ourUser?.position]?.weeks ?? null;
  }, [activeUsers, scheduleRestriction]);

  const activeTeams = useMemo(() => stateTeams.filter(
    (team) => team.active
      && team.members?.length
      && team.divisionIds?.some((dId) => dId === selectedDivision),
  ), [stateTeams, selectedDivision]);
  const teamMap = useMemo(() => getIdMap(stateTeams), [stateTeams]);

  const repeatStartDate = useMemo(() => {
    // Use start date for individual and new new shifts.
    if (!originalShift || !originalShift.startTime) {
      return dateRange ? dateRange[0] : null;
    }
    // Otherwise use original task's start
    return originalShift.startTime || null;
  }, [originalShift, dateRange]);

  const ganttScheduleRow = useMemo(() => {
    if (!ganttScheduleRowId) return {};
    return ganttScheduleRows.find((row) => row.id === ganttScheduleRowId) || {};
  }, [ganttScheduleRowId, ganttScheduleRows]);

  const onFormClick = useCallback(() => {
    if (!formId) return;
    openFormDetail(formId);
  }, [formId, openFormDetail]);

  const onUserClick = useCallback(async (userId) => {
    if (!formRef?.current) return;
    const values = await formRef.current.getFieldsValue();
    const { users: formUsers = [] } = values;
    const uniqueUsers = new Set(formUsers);
    uniqueUsers.add(userId);
    const newUsers = Array.from(uniqueUsers);
    message.success('User added to shift');
    setAssignedUsers(newUsers);
    updateForm({ ref: formRef.current, data: { users: newUsers } });
  }, [formRef.current]);

  const onProjectSelect = useCallback((selectedId) => {
    if (selectedId === selectedProject) return;
    setSelectedProject(selectedId);
    setSelectedPhase();
    setSelectedCC();
  }, [selectedProject]);
  const onPhaseSelect = useCallback((selectedId) => {
    if (selectedId === selectedPhase) return;
    setSelectedPhase(selectedId);
    setSelectedCC();
  }, [selectedPhase]);

  const onTeamSelect = useCallback((selectedIds) => {
    setSelectedTeams(selectedIds);
  }, []);

  const saveShift = async (newShift, shiftIsDraft) => {
    if (shiftIsDraft) {
      setDraftIsLoading(true);
    } else {
      setSaveIsLoading(true);
    }
    if (await onSave(newShift)) {
      setFiles([]);
    }
    setSaveIsLoading(false);
    setDraftIsLoading(false);
  };

  const onSaveClicked = useCallback(async (shiftIsDraft = false) => {
    if (!formRef || !formRef.current) return;
    try {
      try {
        await formRef.current.validateFields();
      } catch (e) {
        const relevantErrors = [];
        const fieldsToIgnore = [
          'expectedUsers',
          'users',
        ];

        e.errorFields.forEach((field) => {
          if (field.name.length !== 1 || !fieldsToIgnore.includes(field.name[0])) {
            relevantErrors.push(field);
          }
        });

        if (relevantErrors.length) {
          return;
        }
      }
      const values = await formRef.current.getFieldsValue();
      const {
        phaseId: newPhaseId,
        dateRange: [start, end] = [],
        color: newColor = null,
        reminders: newReminders,
        externalReminders: newExternalReminders,
        repeatEndDate: newRepeatEndDate,
        teams: newTeams,
        users: newUsers,
        expectedUsers: newExpectedUsers,
        timezone: newTimezone,
        categoryId: newCategoryId,
        inheritCategoryColor: newInheritCategoryColor,
        equipment: newEquipment,
      } = values;

      const newStart = convertFromOneTimezoneToAnother(start, timezone, newTimezone);
      const newEnd = convertFromOneTimezoneToAnother(end, timezone, newTimezone);

      // Determine the final color based on inheritance setting
      let finalColor = newColor;
      if (newInheritCategoryColor && newCategoryId && categoryMap[newCategoryId]?.color) {
        finalColor = categoryMap[newCategoryId].color;
      }

      // Create new shift:
      const newShift = {
        ...values,
        reminders: refineReminders(newReminders),
        externalReminders: refineExternalReminders(newExternalReminders),
        startTime: newStart,
        endTime: newEnd,
        top: timestampToPosition(newStart, day),
        bottom: timestampToPosition(newEnd, day),
        phaseId: newPhaseId === 'Unphased' ? null : newPhaseId,
        color: finalColor,
        timezone: newTimezone,
        formData,
        isDraft: shiftIsDraft,
        materials: selectedMaterials,
        equipment: newEquipment || [],
      };

      if (newRepeatEndDate && moment.isMoment(newRepeatEndDate)) {
        delete newShift.repeatEndDate;
        newShift.repeatEndDate = newRepeatEndDate.startOf('day').valueOf();
      }

      const newUserSet = new Set(newUsers);
      const teamsToAdd = [];
      const teamsToRemove = [];
      newTeams?.forEach((teamId) => {
        let hasTeam = false;
        const team = teamMap[teamId];
        team?.members?.forEach(({ id: memberId }) => {
          if (newUserSet.has(memberId)) hasTeam = true;
        });

        if (hasTeam) teamsToAdd.push(teamId);
        else teamsToRemove.push(team?.name);
      });

      newShift.teams = teamsToAdd;

      const parsedExpectedUsers = newExpectedUsers.filter(({ count, type }) => (
        count > 0 || type === 'default'
      ));

      // expected user differences
      const expectedUserGroupsDiff = Schedule.getExpectedUserGroupsDiff({
        prevExpectedUsers: expectedUsers,
        newExpectedUsers: parsedExpectedUsers,
      });

      Object.entries(expectedUserGroupsDiff).forEach(([key, value]) => {
        newShift[key] = value;
      });

      if (teamsToRemove.length) {
        CustomConfirmModal({
          title: 'Save Confirmation',
          content:
            (
              <>
                <span>
                  The team(s) listed below do not have any members assigned to this shift.
                  Do you want to remove these teams?
                </span>
                <br />
                <ul style={{ paddingTop: 10, textAlign: 'left' }}>
                  {teamsToRemove.map((team) => (
                    <li key={team}>{team}</li>
                  ))}
                </ul>
              </>
            ),
          onOk: () => saveShift(newShift, shiftIsDraft),
          onCancel: async () => {
            newShift.teams = newTeams;
            await saveShift(newShift, shiftIsDraft);
          },
          okText: 'Yes',
          cancelText: 'No',
        });
      } else {
        await saveShift(newShift, shiftIsDraft);
      }
    } catch (err) {
      setSaveIsLoading(false);
      setDraftIsLoading(false);
      Sentry.withScope(() => {
        Sentry.captureException(err);
      });
    }
  }, [
    formRef,
    onSave,
    day,
    formData,
    teamMap,
    timezone,
    categoryMap,
    selectedMaterials,
  ]);

  const handleUpdateStatus = useCallback((userId, newStatus, onPendingChange = false) => {
    setAppliedUsers((prev) => prev.map((user) => {
      if (user.userId === userId) {
        return { ...user, status: (user.status === newStatus && !onPendingChange) ? 'pending' : newStatus };
      }
      return user;
    }));

    if (!formRef.current) return;

    const currentUsers = formRef.current.getFieldValue('users');

    const updatedAppliedUsers = appliedUsers.map((user) => {
      if (user.userId === userId) {
        return { ...user, status: (user.status === newStatus && !onPendingChange) ? 'pending' : newStatus };
      }
      return user;
    });
    const newFinalStatus = updatedAppliedUsers.find((u) => u.userId === userId)?.status;

    const updatedUsers = new Set(currentUsers);
    if (newFinalStatus === 'accepted') {
      updatedUsers.add(userId);
    } else {
      updatedUsers.delete(userId);
    }

    updateForm({
      ref: formRef.current,
      data: { appliedUsers: updatedAppliedUsers, users: Array.from(updatedUsers) },
    });
  }, [formRef, appliedUsers]);

  const handleApplyToggle = useCallback(() => {
    if (!shift) return;

    let newStatus;

    setShiftStatus((prevStatus) => {
      newStatus = (prevStatus === 'pending' || prevStatus === 'accepted') ? 'withdrawn' : 'pending';

      onApply(shift, newStatus);

      let hasSeenUser = false;
      const newAppliedUsers = (shiftShadow?.appliedUsers || []).map((user) => {
        if (user.userId === Permissions.id) {
          hasSeenUser = true;
          return { ...user, status: newStatus };
        }
        return user;
      });

      if (!hasSeenUser) {
        newAppliedUsers.push({ userId: Permissions.id, status: newStatus });
      }

      setShiftShadow(
        createShallowShift({
          ...(shiftShadow || {}),
          applicationStatus: newStatus,
          appliedUsers: newAppliedUsers,
        })
      );
      return newStatus;
    });
  }, [shift, onApply, shiftShadow]);

  const onPendingUsersChange = useCallback((pendingUsers) => {
    if (!isDraft) return;

    const pendingUsersSet = new Set(pendingUsers);
    const appliedUsersSet = new Set(appliedUsers.map((user) => user.userId));

    const toAccept = pendingUsers.filter((userId) => appliedUsersSet.has(userId));

    // Users to reject (users that were applied before but are no longer in pendingUsers)
    const toReject = appliedUsers
      .filter(({ userId, status }) => !pendingUsersSet.has(userId) && status !== 'withdrawn' && status !== 'pending')
      .map(({ userId }) => userId);

    toAccept.forEach((userId) => handleUpdateStatus(userId, 'accepted', true));
    toReject.forEach((userId) => handleUpdateStatus(userId, 'rejected', true));
  }, [isDraft, appliedUsers, handleUpdateStatus]);

  const onFormValuesChanged = useCallback((_, values) => {
    const {
      dateRange: newDateRange = [],
      isEvent: formIsEvent,
      repeat: newRepeat,
      users: newUsers,
      categoryId: newCategoryId,
      inheritCategoryColor: newInheritCategoryColor,
    } = values;
    setIsEvent(formIsEvent);
    setAssignedUsers(newUsers);
    setSelectedCategoryId(newCategoryId);
    setRepeatValue(newRepeat);
    if (!newDateRange || newDateRange.length < 2) return;
    const [newStart, newEnd] = newDateRange;
    if (!dateRange || dateRange[0] !== newStart || dateRange[1] !== newEnd) {
      setDateRange([newStart, newEnd]);
    }

    const newDate = DateTime.fromMillis(newStart).set({ hour: 0, minute: 0, second: 0 });
    const shadow = createShallowShift({
      ...values,
      startTime: newStart,
      endTime: newEnd,
      id,
    });

    setDate(newDate);
    setShiftShadow(shadow);

    // Update color when category changes and inherit is checked
    if (newInheritCategoryColor && newCategoryId && categoryMap[newCategoryId]?.color) {
      updateForm({ ref: formRef.current, data: { color: categoryMap[newCategoryId].color } });
    }
  }, [dateRange, categoryMap, formRef]);

  useEffect(() => {
    if (visible
        && formRef
        && formRef.current
        && !isNullOrUndefined(startTime)
        && !isNullOrUndefined(endTime)) {
      const newRange = [startTime, endTime];
      setDateRange(newRange);
      formRef.current.setFieldsValue({ dateRange: newRange });
    }
  }, [startTime, endTime, visible, formRef]);

  useEffect(() => {
    setFiles(shiftFiles ?? []);
  }, [shiftFiles]);

  useEffect(() => {
    if (groupId) {
      dispatch(getShiftGroup(groupId));
    }
  }, [groupId]);

  useEffect(() => {
    if (ganttScheduleRowId && !isGanttDispatch) {
      dispatch(getGanttScheduleRow(ganttScheduleRowId));
    }
  }, [ganttScheduleRowId]);

  useEffect(() => {
    setSelectedDivision(divisionId || propDivisionId);
  }, [propDivisionId, divisionId]);

  useEffect(() => {
    setIsEvent(shiftIsEvent);
  }, [shiftIsEvent]);

  // Disabling react-hooks/exhaustive-deps
  // ESLint throws a warning saying we dont need formRef.current in the deps array
  // But without it, the form is not updated correctly.
  useEffect(() => {
    updateForm({ ref: formRef.current, data: { projectId: selectedProject } });
  }, [selectedProject, formRef.current]);

  useEffect(() => {
    updateForm({ ref: formRef.current, data: { phaseId: selectedPhase } });
  }, [selectedPhase, formRef.current]);

  useEffect(() => {
    updateForm({ ref: formRef.current, data: { costcodeId: selectedCC } });
  }, [selectedCC, formRef.current]);

  useEffect(() => {
    updateForm({ ref: formRef.current, data: { teams: selectedTeams } });
  }, [selectedTeams, formRef.current]);

  useEffect(() => {
    const {
      [selectedProject]: {
        divisionId: projectDivisionId,
      } = {},
    } = projectMap;
    if (!config.showDivisions) return;
    if (projectDivisionId !== selectedDivision) {
      updateForm({
        ref: formRef.current,
        data: { projectId: null, phaseId: null, costcodeId: null },
      });
    }
    if (selectedDivision && formRef && formRef.current) {
      const {
        [selectedDivision]: {
          users: divisionUsers = new Set(),
        } = {},
      } = divisions;

      const teamMembers = [];
      selectedTeams?.forEach((selectedTeam) => {
        const {
          [selectedTeam]: {
            members = [],
          } = {},
        } = teamMap;

        teamMembers.push(...members);
      });

      const teamUsers = new Set(teamMembers.map(getId));
      const formUsers = formRef.current.getFieldValue('users') || [];

      const ourUsers = formUsers
        .map((userId) => userMap[userId])
        .filter((user) => user);
      const newUsers = ourUsers
        .filter((user) => (
          divisionUsers.has(user.id) && (
            !selectedTeams?.length || teamUsers.has(user.id)
          )
        ))
        .map((user) => user.id);
      updateForm({
        ref: formRef.current,
        data: {
          users: newUsers,
        },
      });
      setAssignedUsers(newUsers);
    }
  }, [
    selectedDivision,
    divisions,
    selectedProject,
    projectMap,
    userMap,
    formRef.current,
    selectedTeams,
    teamMap,
  ]);

  // Used to close MonthlyMoreView
  useEffect(() => {
    dispatch(toggleShiftView(visible));
  }, [dispatch, visible]);

  const initialCostcodeId = useMemo(() => {
    if (!enableGlobalCCOnProject || !costcodeId) return costcodeId;

    const costcode = costcodeMap[costcodeId];
    const isGlobalCostcode = costcode && !costcode.projectId;

    if (isGlobalCostcode) {
      return `global.${costcodeId}`;
    }

    const phaseForKey = phaseId === null || phaseId === 'Unphased' || phaseId === 'unphased' ? null : phaseId;
    return phaseForKey ? `${phaseForKey}.${costcodeId}` : `unphased.${costcodeId}`;
  }, [enableGlobalCCOnProject, costcodeId, costcodeMap, phaseId]);

  useEffect(() => {
    if (visible) {
      setSelectedProject(projectId || propProjectId);
      const ourPhase = phaseId === null && initialCostcodeId ? 'Unphased' : phaseId;
      setSelectedPhase(ourPhase);
      setSelectedCC(initialCostcodeId);
      setRepeatValue(repeat);
      setSelectedTeams(teams || []);
      setSelectedCategoryId(categoryId);
      setSelectedMaterials(shift?.materials || []);
    } else {
      setSelectedProject();
      setSelectedPhase();
      setSelectedCC();
      setDateRange();
      setRepeatValue();
      setSelectedTeams([]);
      setSelectedCategoryId();
      setSelectedMaterials([]);
    }
  }, [visible, projectId, propProjectId, phaseId, initialCostcodeId, teams, categoryId, shift]);

  const divProjects = useMemo(() => {
    if (!config.showDivisions) return activeProjects;
    return activeProjects.filter((project) => project.divisionId === selectedDivision);
  }, [activeProjects, selectedDivision]);

  const divUsers = useMemo(() => {
    let filteredUsers = activeUsers;
    const teamMembers = [];
    selectedTeams?.forEach((selectedTeam) => {
      const {
        [selectedTeam]: {
          members = [],
        } = {},
      } = teamMap;

      teamMembers.push(...members);
    });

    if (selectedTeams?.length) {
      const teamMemberSet = new Set(teamMembers.map(getId));
      filteredUsers = filteredUsers.filter((user) => teamMemberSet.has(user.id));
    }

    if (!config.showDivisions) return filteredUsers;
    const {
      [selectedDivision]: {
        users: divisionUsers = new Set(),
      } = {},
    } = divisions;
    return filteredUsers.filter((user) => divisionUsers.has(user.id));
  }, [activeUsers, divisions, selectedDivision, selectedTeams, teamMap]);

  const {
    phases,
    phaseMap: phaseIdMap,
  } = useMemo(() => {
    const {
      phases: parsedPhases,
      phaseMap: parsedPhaseMap,
    } = getProjectPhases({
      costcodes: activeCostcodes,
      phases: activePhases,
      projectId: selectedProject,
    });

    return {
      phases: parsedPhases,
      phaseMap: parsedPhaseMap,
    };
  }, [activeCostcodes, activePhases, selectedProject]);

  const phaseOptions = useMemo(() => {
    const opts = [...phases];

    if (enableGlobalCCOnProject
      && selectedProject
      && !phases.find((phase) => phase.id === UNPHASED)
    ) {
      opts.unshift(unphasedCode);
    }

    return opts;
  }, [phases]);

  const onlyUnphased = phaseOptions.length === 1 && phaseOptions[0].id === UNPHASED;

  const costcodeOptions = useMemo(() => {
    if (!selectedPhase) return [];
    const {
      [selectedPhase]: {
        costcodes: phaseCC = [],
      } = {},
    } = phaseIdMap;
    const list = phaseCC.filter((cc) => cc.canClockIn);
    return selectedCC && costcodeMap[selectedCC] && !list.some((cc) => cc.id === selectedCC)
      ? [...list, costcodeMap[selectedCC]] : list;
  }, [selectedPhase, phaseIdMap]);

  const materialOptions = useMemo(() => getFilteredMaterials({
    materials,
    selectedDivisions: selectedDivision ? new Set([selectedDivision]) : new Set(),
    selectedMaterials: new Set((shift?.materials || []).map((material) => material.id)),
  }), [
    materials,
    selectedDivision,
    shift,
  ]);

  const equipmentOptions = useMemo(() => getFilteredEquipment({
    equipment,
    selectedDivisions: selectedDivision ? new Set([selectedDivision]) : new Set(),
    selectedEquipment: new Set(shift?.equipment || []),
  }), [
    equipment,
    selectedDivision,
    shift,
  ]);

  const equipmentMap = useMemo(() => getIdMap(equipment), [equipment]);

  const onMaterialSubmit = useCallback((newMaterials) => {
    setSelectedMaterials(newMaterials);
    setShowMaterialDrawer(false);
  }, []);

  const onSelectChange = useCallback((val, option) => {
    if (formRef.current) {
      formRef.current.setFieldsValue({
        [option]: val,
      });
    }
  }, []);

  const getNewShiftReminders = (config) => {
    if (config?.reminders) return config.reminders;
    let newShiftReminders = [REMINDER_OPTIONS.DEFAULT_REMINDER];
    try {
      const lastReminderConfig = JSON.parse(window.sessionStorage.getItem('lastReminderConfig'));
      if (lastReminderConfig) newShiftReminders = reformatRemindersFromValues(lastReminderConfig);
    } catch (error) {
      console.error(error);
    }
    return newShiftReminders;
  };

  const article = isEvent ? 'an' : 'a';
  const type = isEvent ? 'Event' : 'Shift';

  const showRepeatStart = repeatValue
    && repeatStartDate
    && originalShift
    && originalShift.id !== id;
  const startDateDT = (repeatStartDate && Number.isInteger(repeatStartDate))
    ? DateTime.fromMillis(repeatStartDate)
    : null;
  const startDateText = startDateDT
    ? startDateDT.toLocaleString(DateTime.DATE_MED)
    : 'Not Set';

  const endDateText = (repeatEndDate && Number.isInteger(repeatEndDate))
    ? DateTime.fromMillis(repeatEndDate).toLocaleString(DateTime.DATE_MED)
    : 'Not Set';
  const parsedRepeatEndDate = useMemo(() => (
    repeatEndDate ? moment(repeatEndDate) : undefined
  ), [
    repeatEndDate,
  ]);
  const repeatEndValidator = useCallback(() => {
    if (!formRef || !formRef.current) return Promise.reject();
    const values = formRef.current.getFieldsValue();
    const {
      dateRange: [newStartTime] = [],
      repeat: newRepeat,
      repeatEndDate: newRepeatEndDateMoment,
    } = values;
    if (!newRepeat) {
      return Promise.resolve();
    }
    const newRepeatEndDate = newRepeatEndDateMoment && moment.isMoment(newRepeatEndDateMoment)
      ? newRepeatEndDateMoment.valueOf()
      : null;
    return validateRepeatEndDate({
      startDate: newStartTime,
      repeat: newRepeat,
      repeatEndDate: newRepeatEndDate,
      type: 'Start Time',
    });
  }, [formRef]);

  const onTeamClick = useCallback((teamId, shouldAdd = false) => {
    if (!formRef || !formRef.current) return;
    const values = formRef.current.getFieldsValue();
    const { users: formUsers = [] } = values;

    let newUsers;

    if (!shouldAdd) {
      const availableTeamMembers = teamMap[teamId]?.members || [];
      const availableTeamMemberSet = new Set(availableTeamMembers.map(getId));
      newUsers = formUsers.filter((userId) => !availableTeamMemberSet.has(userId));
    } else {
      const divUsersSet = new Set(divUsers.map(getId));
      newUsers = [
        ...new Set(
          [
            ...formUsers,
            ...(teamMap[teamId]?.members
              .map(getId)
              ?.filter((userId) => divUsersSet.has(userId)) || []),
          ],
        ),
      ];
    }

    newUsers = Array.from(newUsers);
    updateForm({ ref: formRef.current, data: { users: newUsers } });
    setAssignedUsers(newUsers);
  }, [formRef, divUsers, teamMap]);

  // Side View Calendar-related functions

  useEffect(() => {
    const loadShifts = async ({ startTime: start, endTime: end }) => {
      if (await dispatch(getSchedule({ startTime: start, endTime: end }))) {
        setLastDownload([start, end]);
      }
    };
    if (!visible || hideCalendar) return;
    const startDT = date.startOf('month').minus({ day: 6 });
    const endDT = date.endOf('month').plus({ day: 6 });
    const start = startDT.toMillis();
    const end = endDT.toMillis();

    const hasScheduleRestriction = !isNullOrUndefined(weeksRestriction);
    if (hasScheduleRestriction && getMaxStartFromRestriction(weeksRestriction) < start) return;

    if ((!lastDownload?.length || lastDownload[0] > start || lastDownload[1] < end)) {
      loadShifts({ startTime: start, endTime: end });
    }
  }, [lastDownload, date, viewType, visible, weeksRestriction, hideCalendar]);

  useEffect(() => {
    if (day?.isValid) {
      setDate(day);
    }
  }, [day]);

  useEffect(() => {
    if (hideCalendar) return;
    if (startTime && endTime) {
      const shadow = createShallowShift(shift);
      setShiftShadow(shadow);
    }
  }, [startTime, endTime, shift]);

  useEffect(() => {
    if (hideCalendar) return;
    const { startTime: start, endTime: end } = shiftShadow;

    if (start && end) {
      const newShifts = addShallowShiftToShifts({
        shifts,
        shift: shiftShadow,
        shiftMap,
      });
      setLocalShifts(newShifts);
    }
  }, [shifts, shiftShadow, shiftMap]);

  const onDateChange = useCallback((newDate) => {
    const ts = newDate.valueOf();
    const dt = DateTime.fromMillis(ts);
    setDate(dt);
  }, []);

  const onDaySelect = useCallback((newDay) => {
    setDate(newDay);
    setViewType('Day');
  }, []);

  const typeMap = {
    Day: 'date',
    Week: 'week',
  };

  const header = useMemo(() => {
    const hover = (
      <HoverHelp
        content={`This shift was dispatched from a gantt task ${ganttScheduleRow.name ?? ''}.`}
        Icon={InfoCircleTwoTone}
        iconProps={{ twoToneColor: '#fdb81b', style: { height: 20, fontSize: '18px' } }}
      />
    );

    const label = title || `Schedule ${article} ${type}`;

    if (!ganttScheduleRowId || isGanttDispatch) return label;

    return (
      <Row style={{
        display: 'flex', flexDirection: 'row', justifyContent: 'space-between',
      }}
      >
        <Col style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
          {Permissions.has('PROJECT_GANTT_SCHEDULE_READ') ? (
            <Button
              type="link"
              onClick={() => setShowGanttDrawer(true)}
              style={{ marginTop: -3 }}
            >
              {hover}
            </Button>
          ) : <div>{hover}</div>}
          {label}
        </Col>

      </Row>
    );
  }, [title, article, type, ganttScheduleRow, ganttScheduleRowId]);

  const remindersClose = useCallback(() => setShowRemindersDrawer(false));
  const remindersOpen = useCallback(() => setShowRemindersDrawer(true));

  const isNewShift = isNew || !id;

  return (
    <>
      <Drawer
        title={header}
        width={hideCalendar ? 750 : 1250}
        visible={visible}
        maskClosable={isDisplay}
        onClose={onClose}
        style={{
          zIndex: isShiftTable ? 1000 : 150,
          ...style,
        }}
      >
        <Row
          gutter={12}
          style={{
            maxHeight: 'calc(100vh - 110px)',
            overflow: 'hidden',
          }}
        >
          {hideCalendar ? null : (
            <Col
              span={12}
              style={{
                maxHeight: 'calc(100vh - 135px)',
                overflow: 'hidden',
                paddingBottom: '4em',
              }}
            >
              <ScheduleDatePicker
                viewType={viewType}
                onViewTypeChange={setViewType}
                onDateChange={onDateChange}
                date={date}
                onVisibleChanged={() => {}}
                typeMap={typeMap}
              />
              {viewType === 'Week' && (
              <WeeklySchedule
                date={date}
                shifts={localShifts}
                onShiftCreate={() => {}}
                onNewShiftChange={() => {}}
                onShiftSelect={() => {}}
                onShiftEdit={() => {}}
                onDaySelect={onDaySelect}
              />
              )}
              {viewType === 'Day' && (
              <DailySchedule
                day={date}
                shifts={localShifts[getDayKey(date)]}
                onShiftCreate={() => {}}
                onNewShiftChange={() => {}}
                onShiftSelect={() => {}}
                onShiftEdit={() => {}}
              />
              )}
            </Col>
          )}

          <Col
            span={hideCalendar ? 24 : 12}
            style={{
              maxHeight: 'calc(100vh - 135px)',
              overflowY: 'scroll',
              overflowX: 'hidden',
            }}
          >
            <Form
              layout="vertical"
              key={`${id} ${files.length} ${visible}`}
              ref={formRef}
              style={{
                padding: '5em 0',
                ...formStyle,
              }}
              onValuesChange={onFormValuesChanged}
              initialValues={{
                isEvent: shiftIsEvent,
                dateRange,
                timezone,
                reminders: (isNewShift) ? getNewShiftReminders(reminderConfig) : reformatRemindersFromValues(reminders),
                externalReminders: (isNewShift)
                  ? (reminderConfig?.externalReminders ?? [{ value: REMINDER_OPTIONS.DEFAULT_REMINDER, recipients: [] }])
                  : reformatExternalReminders(externalReminders),
              }}
            >
              { !isShiftTable && (
                <Form.Item
                  name="isEvent"
                  className="schedule-form-item"
                  initialValue={!!isEvent}
                >
                  <OnTraccrSwitch
                    disabled={isDisplay}
                    title={type}
                  />
                </Form.Item>
              )}
              <Form.Item
                name="title"
                label="Title"
                className="schedule-form-item"
                labelCol={formLabelStyle}
                initialValue={title}
                rules={[{ required: !isDisplay, message: 'Please enter a title' }]}
              >
                {getFormView({
                  isDisplay,
                  value: title,
                  input: <OnTraccrTextInput placeholder="Add a title" />,
                })}
              </Form.Item>

              {config.showDivisions
              && (
              <Form.Item
                name="divisionId"
                key="divisionId"
                label="Division"
                style={{ marginBottom: 0 }}
                rules={[{ required: !isDisplay, message: 'Select a division' }]}
                valuePropName="divisionId"
                initialValue={divisionId || propDivisionId}
              >
                <DivisionSelector
                  displayMode={isDisplay || propDivisionId}
                  onChange={(newDivision) => setSelectedDivision(newDivision)}
                />
              </Form.Item>
              )}
              <Form.Item
                name="dateRange"
                label="Dates"
                className="schedule-form-item"
                labelCol={formLabelStyle}
                rules={[
                  { required: !isDisplay, message: 'Please select a start and end date' },
                  { validator: dateRangeValidator },
                ]}
              >
                <OnTraccrDatePicker isDisplay={isDisplay} timezone={timezone} />
              </Form.Item>
              <Form.Item
                name="repeat"
                className="schedule-form-item"
                initialValue={repeat}
              >
                <ScheduleDateRepeatSelector
                  isDisplay={isDisplay}
                  startTime={repeatStartDate}
                  onChange={setRepeatValue}
                  allowBiweekly
                />
              </Form.Item>

              { showRepeatStart && (
              <Form.Item
                label="Repeat Start Date"
                style={{ marginBottom: 10 }}
              >
                {
                isDisplay
                  ? (
                    <DisplayText
                      label="Repeat Start Date"
                      title={startDateText}
                    />
                  )
                  : (
                    <DatePicker
                      label="Repeat Start Date"
                      format="MMM Do YY"
                      value={moment(repeatStartDate)}
                      style={{ width: 200 }}
                      disabled
                    />
                  )
                }
              </Form.Item>
              )}
              { repeatValue && (
              <Form.Item
                label="Repeat End Date"
                name="repeatEndDate"
                style={{ marginBottom: 10 }}
                rules={[{ required: !isDisplay, validator: repeatEndValidator }]}
                initialValue={parsedRepeatEndDate}
              >
                {isDisplay
                  ? <DisplayText title={endDateText} />
                  : <DatePicker
                      format="MMM Do YY"
                      allowClear
                      style={{ width: 200 }}
                    />}
              </Form.Item>
              )}

              <Form.Item
                name="timezone"
                label="Timezone"
                className="schedule-form-item"
                labelCol={formLabelStyle}
                initialValue={timezone}
                style={{ marginBottom: 10 }}
              >
                {getFormView({
                  isDisplay,
                  value: timezone,
                  input: (
                    <TimezoneSelector
                      placeholder="Select timezone"
                      showSearch
                      style={{ width: 200, textAlign: 'left' }}
                    />
                  ),
                })}
              </Form.Item>
              <Form.Item
                name="reminders"
                label="Reminders"
                className="schedule-form-item"
                labelCol={formLabelStyle}
                initialValue={reminders}
              >
                <OnTraccrButton
                  title="Reminders"
                  onClick={remindersOpen}
                  style={{ width: 200 }}
                  icon={<SettingOutlined />}
                />
              </Form.Item>
              {!isEvent && (
                <>
                  <Form.Item
                    name="projectId"
                    label={t('Project')}
                    className="schedule-form-item"
                    labelCol={formLabelStyle}
                    initialValue={projectId || propProjectId}
                  >
                    {getFormView({
                      isDisplay: isDisplay || projectLocked,
                      value: getFromMap({ id: projectId || propProjectId, idMap: projectMap }),
                      input: (
                        <Select
                          placeholder={`Select a ${t('Project')}`}
                          optionLabelProp="label"
                          optionFilterProp="label"
                          showSearch
                          onSelect={onProjectSelect}
                          onChange={(selected) => {
                            if (!selected) {
                              setSelectedPhase();
                              setSelectedCC();
                            }
                          }}
                          allowClear
                          filterOption={filterSelectDropdown}
                        >
                          {basicOptions(divProjects, (item) => formatProjectLabelFromCompanySettings({
                            name: item.name,
                            number: item.number,
                            settings,
                          }))}
                        </Select>
                      ),
                    })}
                  </Form.Item>
                  <Form.Item
                    name="phaseId"
                    label="Phase"
                    className="schedule-form-item"
                    labelCol={formLabelStyle}
                    initialValue={phaseId}
                  >
                    {getFormView({
                      isDisplay,
                      value: getFromMap({
                        id: phaseId,
                        idMap: phaseIdMap,
                        defaultValue: costcodeId ? 'Unphased' : null,
                      }),
                      input: (
                        <Select
                          placeholder="Select a Phase"
                          optionLabelProp="label"
                          optionFilterProp="label"
                          showSearch
                          disabled={!selectedProject}
                          onSelect={onPhaseSelect}
                          onChange={(selected) => {
                            if (!selected) setSelectedCC();
                          }}
                          allowClear
                        >
                          {basicOptions(phaseOptions)}
                        </Select>
                      ),
                    })}
                  </Form.Item>
                  <Form.Item
                    name="costcodeId"
                    label="Costcode"
                    className="schedule-form-item"
                    labelCol={formLabelStyle}
                    initialValue={initialCostcodeId}
                  >
                    {getFormView({
                      isDisplay,
                      value: getFromMap({
                        id: costcodeId,
                        idMap: costcodeMap,
                        formatter: (cc) => `${cc.code} - ${cc.name}`,
                      }),
                      input: (enableGlobalCCOnProject ? (
                        <CostcodesTreeSelector
                          projectId={selectedProject}
                          phaseId={selectedPhase}
                          divisionId={selectedDivision}
                          isClockIn
                          disabled={
                            !selectedProject
                            || (!onlyUnphased && !selectedPhase)
                          }
                          onSelect={(selectedId) => setSelectedCC(selectedId)}
                        />
                      ) : (
                        <Select
                          placeholder="Select a Cost Code"
                          optionLabelProp="label"
                          optionFilterProp="label"
                          showSearch
                          disabled={!selectedProject || !selectedPhase}
                          onSelect={(selectedId) => setSelectedCC(selectedId)}
                          allowClear
                        >
                          {basicOptions(costcodeOptions, ({ label }) => label)}
                        </Select>
                      )),
                    })}
                  </Form.Item>
                </>
              )}
              <Form.Item
                name="color"
                label="Color"
                style={{ marginBottom: isDisplay ? 0 : 10, maxWidth: 150 }}
                labelCol={{
                  style: {
                    paddingBottom: 0,
                    marginTop: 5,
                  },
                }}
                initialValue={color || undefined}
              >
                <FormColorPicker
                  isNotDisplay={!isDisplay || (selectedCategoryId && formRef.current?.getFieldValue('inheritCategoryColor'))}
                  allowClear
                />
              </Form.Item>
              <Form.Item
                name="teams"
                label="Team"
                className="schedule-form-item"
                labelCol={formLabelStyle}
                initialValue={teams}
              >
                <ScheduleTeamSelector
                  isDisplay={isDisplay}
                  teamMap={teamMap}
                  getFromMap={getFromMap}
                  getFormView={getFormView}
                  basicOptions={basicOptions}
                  activeTeams={activeTeams}
                  onChange={onTeamSelect}
                  onTeamClick={onTeamClick}
                />
              </Form.Item>
              {(!isDisplay || Permissions.has('SCHEDULE_DRAFTS_VIEW_ALL')) && !isEvent && !isNew && isDraft && appliedUsers && appliedUsers.length > 0 ? (
                <Form.Item
                  name="appliedUsers"
                  label="Applied Users"
                  className="schedule-form-item"
                >
                  <ScheduleApplicantSelector
                    entireUserMap={userMap}
                    users={selectedDivision ? divUsers : activeUsers}
                    appliedUsers={appliedUsers}
                    onUpdateStatus={handleUpdateStatus}
                    hasWritePerm={!isDisplay}
                    project={projectMap[selectedProject]}
                  />
                </Form.Item>
              ) : null}

              { !(isDraft && !(Permissions.has('SCHEDULE_DRAFTS_VIEW_ALL') || !isDisplay)) && (
              <Form.Item
                name="users"
                label={isDraft ? 'Pending Users' : 'Assigned Users'}
                className="schedule-form-item"
                labelCol={formLabelStyle}
                initialValue={users}
                rules={[
                  {
                    validator: () => validateExpectedUsers({
                      form: formRef?.current,
                      userToLabel,
                      userLabelMap,
                      userMap,
                      positionMap,
                    }),
                  },
                ]}
              >
                {isDisplay ? (
                  users.map((userId) => (
                    <OntraccrTag key={userId}>
                      {getFromMap({ id: userId, idMap: userMap })}
                    </OntraccrTag>
                  ))
                ) : (
                  <ScheduleUserSelector
                    users={selectedDivision ? divUsers : activeUsers}
                    project={projectMap[selectedProject]}
                    onUserClick={onUserClick}
                    dateRange={dateRange}
                    showLabels
                    assignedUsers={assignedUsers}
                    shiftId={id}
                    onChange={onPendingUsersChange}
                  />
                )}
              </Form.Item>
              )}
              <Form.Item
                name="expectedUsers"
                label="Expected User Groups"
                className="error-warning-form-item"
                initialValue={expectedUsers?.length ? expectedUsers : [{ type: 'default', id: null, count: 0 }]}
                rules={[
                  {
                    validator: () => validateExpectedUsers({
                      form: formRef?.current,
                      userToLabel,
                      userLabelMap,
                      userMap,
                      positionMap,
                    }),
                  },
                ]}
              >
                <ScheduleExpectedUsersInput readOnly={isDisplay} />
              </Form.Item>
              {!isEvent && (
                <>
                  {
                    materialOptions.length > 0
                      && (
                        <>
                          <p className="form-label">Materials</p>
                          <Select
                            mode="multiple"
                            disabled
                            value={selectedMaterials.map((item) => item.id)}
                            options={materialOptions.map((item) => ({ value: item.id, label: item.name }))}
                            placeholder="No Materials"
                            style={{ paddingBottom: 10 }}
                          />
                          <OnTraccrButton
                            type="primary"
                            title="Manage Materials"
                            onClick={() => setShowMaterialDrawer(true)}
                            disabled={isDisplay}
                          />
                          <br />
                          <br />
                        </>
                      )
                  }

                  {
                    equipmentOptions.length > 0
                      && (
                        <Form.Item
                          name="equipment"
                          label="Equipment"
                          className="schedule-form-item"
                          labelCol={formLabelStyle}
                          initialValue={shift?.equipment || []}
                        >
                          {getFormView({
                            isDisplay,
                            value: shift?.equipment?.map((id) => getFromMap({
                              id,
                              idMap: equipmentMap,
                              formatter: (item) => formatEquipmentLabelFromCompanySettings({
                                name: item.name,
                                code: item.code,
                                settings,
                              }),
                            })).filter(Boolean).join(', '),
                            input: (
                              <Select
                                mode="multiple"
                                onChange={(val) => onSelectChange(val, 'equipment')}
                                allowClear
                                options={equipmentOptions.map((item) => ({
                                  value: item.id,
                                  label: formatEquipmentLabelFromCompanySettings({
                                    name: item.name,
                                    code: item.code,
                                    settings,
                                  }),
                                }))}
                                placeholder="Equipment"
                                optionFilterProp="label"
                              />
                            ),
                          })}
                        </Form.Item>
                      )
                  }
                </>
              )}
              {!isEvent && (
                <Form.Item
                  name="categoryId"
                  label="Category"
                  className="schedule-form-item"
                  labelCol={formLabelStyle}
                  initialValue={categoryId}
                >
                  {getFormView({
                    isDisplay,
                    value: getFromMap({ id: categoryId, idMap: categoryMap }),
                    input: (
                      <Select
                        placeholder="Select a Category"
                        optionLabelProp="label"
                        optionFilterProp="label"
                        showSearch
                        allowClear
                      >
                        {basicOptions(categories)}
                      </Select>
                    ),
                  })}
                </Form.Item>
              )}
              {!isEvent && selectedCategoryId && (
                <Form.Item
                  name="inheritCategoryColor"
                  className="schedule-form-item"
                  initialValue={false}
                >
                  <OnTraccrCheckbox
                    label="Inherit category color"
                    disabled={isDisplay}
                  />
                </Form.Item>
              )}
              {!isEvent && (
              <Form.Item
                name="description"
                label="Description"
                className="schedule-form-item"
                labelCol={formLabelStyle}
                initialValue={description}
              >
                {getFormView({
                  isDisplay,
                  value: description,
                  input: <OnTraccrTextInput textarea />,
                })}
              </Form.Item>
              )}
              {!isEvent && (
              <Form.Item
                name="shouldLockClockIn"
                className="schedule-form-item"
                initialValue={!!shouldLockClockIn}
              >
                <OnTraccrCheckbox
                  label="Should Lock Clock In Details"
                  disabled={isDisplay}
                />
              </Form.Item>
              )}

              {formId && (
              <Form.Item
                label="Form"
                className="schedule-form-item"
                labelCol={formLabelStyle}
              >
                <BorderlessButton
                  onClick={onFormClick}
                  style={{ textAlign: 'left' }}
                  title={(
                    <span>
                      <FormOutlined style={{ marginRight: 10 }} /> {`${formTemplateName}${formNumber ? ` - ${formNumber}` : ''}`}
                    </span>
                )}
                />
              </Form.Item>
              )}

              {!isEvent && (
              <Form.Item
                name="files"
                label="Attachments"
                className="schedule-form-item"
                labelCol={formLabelStyle}
                initialValue={files}
                valuePropName="files"
              >
                <FileReelWithUpload
                  isDisplay={isDisplay}
                />
              </Form.Item>
              )}
              <ScheduleEventForm
                isDisplay={isDisplay}
                initialValue={formTemplateId}
                formRef={formRef}
                onFormDataChanged={setFormData}
                shiftId={id}
                isNew={isNew}
                isEvent={isEvent}
                visible={visible}
              />
              <ScheduleReminderDrawer
                form={formRef}
                isDisplay={isDisplay}
                visible={showRemindersDrawer}
                onClose={remindersClose}
                selectedDivisionId={selectedDivision}
                selectedProjectId={selectedProject}
                sections={sections}
              />
            </Form>
          </Col>
        </Row>
        {!isDisplay && (
        <div className="drawer-footer">
          <Row justify="space-between" gutter={10}>
            <Col>
              {!isNew && (
              <OnTraccrButton
                title="Delete"
                type="back"
                onClick={() => onDelete(shift)}
              />
              )}
              {isNew && !isEvent && (
              <OnTraccrButton
                title="Upload"
                type="back"
                onClick={() => {
                  onClose();
                  setShowUploadDrawer(true);
                }}
              />
              )}
            </Col>
            <Col>
              <Row>
                <OnTraccrButton
                  title="Cancel"
                  type="cancel"
                  style={{ marginRight: 8 }}
                  onClick={onClose}
                />
                {
                  (!isEvent && (isDraft || isNew)) ? (
                    <OnTraccrButton
                      icon={<SaveOutlined />}
                      title="Save Draft"
                      style={{ marginRight: 8 }}
                      loading={draftIsLoading}
                      onClick={() => onSaveClicked(true)}
                    />
                  ) : null
                }
                <OnTraccrButton
                  title="Submit"
                  htmlType="submit"
                  loading={saveIsLoading}
                  onClick={() => onSaveClicked(false)}
                />
              </Row>
            </Col>
          </Row>
        </div>
        )}
        {/* Should display apply if these conditions are met:
        1. The user does not have write permissions.
        2. The shift is a draft.
        3. The user has not been rejected from the shift.
        */}
        {
          isDisplay && isDraft && (
            <div className="drawer-footer">
              <Row>
                <Col style={{ marginLeft: 'auto' }}>
                  <Row>
                    {shiftStatus === 'rejected' ? (
                      <OntraccrTag color="red" style={{ padding: '5px 10px', fontSize: '14px' }}>
                        Rejected
                      </OntraccrTag>
                    ) : (
                      <OnTraccrButton
                        title={shiftStatus === 'pending' || shiftStatus === 'accepted' ? 'Unapply' : 'Apply'}
                        onClick={handleApplyToggle}
                      />
                    )}
                  </Row>
                </Col>
              </Row>
            </div>
          )
        }
        <GanttScheduleAddDrawer
          userLabelMap={userLabelMap}
          positionMap={positionMap}
          row={ganttScheduleRow}
          readOnly
          visible={showGanttDrawer && ganttScheduleRow && !Common.isObjectEmpty(ganttScheduleRow)}
          onClose={() => setShowGanttDrawer(false)}
          isShiftConnections
        />
        <MaterialTableAddDrawer
          visible={showMaterialDrawer}
          onClose={() => setShowMaterialDrawer(false)}
          onSubmit={onMaterialSubmit}
          selected={selectedMaterials}
          showInput
          materials={getIdMap(materialOptions)}
          zIndex={style?.zIndex ? style.zIndex + 1 : 1002}
        />
      </Drawer>

      <ShiftUploadDrawer
        visible={showUploadDrawer}
        onClose={() => setShowUploadDrawer(false)}
      />
    </>
  );
}

ScheduleDrawer.propTypes = {
  isNew: PropTypes.bool,
  visible: PropTypes.bool,
  isDisplay: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  openFormDetail: PropTypes.func.isRequired,
  shift: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    day: PropTypes.instanceOf(DateTime),
    startTime: PropTypes.number,
    endTime: PropTypes.number,
    projectId: PropTypes.string,
    phaseId: PropTypes.string,
    costcodeId: PropTypes.string,
    description: PropTypes.string,
    divisionId: PropTypes.string,
    users: PropTypes.arrayOf(PropTypes.string),
    expectedUsers: PropTypes.arrayOf(PropTypes.shape({
      type: PropTypes.string,
      id: PropTypes.string,
      count: PropTypes.number,
    })),
    files: PropTypes.arrayOf(PropTypes.shape({})),
    formId: PropTypes.string,
    isEvent: PropTypes.bool,
    formTemplateId: PropTypes.string,
    formTemplateName: PropTypes.string,
    formNumber: PropTypes.string,
    color: PropTypes.string,
    reminders: PropTypes.arrayOf(PropTypes.shape({})),
    repeat: PropTypes.string,
    repeatEndDate: PropTypes.number,
    groupId: PropTypes.string,
    shouldLockClockIn: PropTypes.bool,
    ganttScheduleRowId: PropTypes.string,
  }),
  shifts: PropTypes.shape({}),
  shiftMap: PropTypes.shape({}),
  lastDownload: PropTypes.arrayOf(PropTypes.number),
  setLastDownload: PropTypes.func.isRequired,
  style: PropTypes.shape({}),
  hideCalendar: PropTypes.bool,
  isGanttDispatch: PropTypes.bool,
  onApply: PropTypes.func,
  isShiftTable: PropTypes.bool,
  formStyle: PropTypes.shape({}),
  divisionId: PropTypes.string,
  projectId: PropTypes.string,
  projectLocked: PropTypes.bool,
  reminderConfig: PropTypes.shape({
    reminders: PropTypes.arrayOf(PropTypes.shape({})),
    externalReminders: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  sections: formSectionsPropType,
};

ScheduleDrawer.defaultProps = {
  isNew: false,
  visible: false,
  isDisplay: false,
  shift: {
    assignedUsers: [{
      type: 'default',
      id: null,
      count: 0,
    }],
  },
  shifts: {},
  shiftMap: {},
  lastDownload: [],
  style: {},
  hideCalendar: false,
  isGanttDispatch: false,
  onApply: () => {},
  isShiftTable: false,
  formStyle: {},
  divisionId: null,
  projectId: null,
  projectLocked: false,
  reminderConfig: undefined,
  sections: [],
};
